/**
 * Worklist sidebar cohort cleanup.
 *
 * Real worklists accumulate dozens of cohorts across device versions and
 * calibration runs; most of the old ones can't actually be opened because
 * their studies pre-date canonicalize or their model version is no longer
 * supported by the viewer. This filter trims the list to:
 *
 * 1. Cohorts with at least one study (`total > 0`). Cohorts at 0/0 are
 *    drift artifacts that should never have made the list.
 * 2. Cohorts whose device family is NOT in `DEPRECATED_FAMILY_PREFIXES`.
 *    These are entire product lines that have been superseded — even
 *    their latest -v<X.Y.Z> doesn't render correctly in the current viewer.
 *    Maintained as products deprecate.
 * 3. The latest semver-suffixed version per device family (for the families
 *    that survive (2)). A family is the model string with the trailing
 *    `-v<X.Y.Z>` stripped; cohorts whose model has no version suffix are
 *    treated as their own family of one and kept as-is.
 *
 * The viewer's actual canonicalization gate is enforced server-side (the
 * metadata endpoint returns 423 for non-canonicalized studies); this
 * predicate just keeps the picker honest by hiding the stale entries.
 */
import type { Cohort } from "../hooks/useStudyQueries";

/**
 * Device-family prefixes whose entire history is deprecated — every cohort
 * with a model starting with one of these is hidden, regardless of version.
 *
 * Add a family here when a product line is superseded. Match is prefix-
 * based; "a2z-abdomen-pelvis-ct-suite" hides the bare family + its `-full`
 * + `-pre-validation` siblings in one entry.
 */
const DEPRECATED_FAMILY_PREFIXES: readonly string[] = ["a2z-abdomen-pelvis-ct-suite", "a2z-cortex"];

function isDeprecatedFamily(model: string): boolean {
  return DEPRECATED_FAMILY_PREFIXES.some((prefix) => model.startsWith(prefix));
}

interface Parsed {
  cohort: Cohort;
  family: string;
  /** null when the model string has no `-v<X.Y.Z>` suffix. */
  version: number[] | null;
}

function parseModel(model: string): { family: string; version: number[] | null } {
  const m = model.match(/^(.+?)-v(\d+(?:\.\d+)*)$/);
  if (!m) return { family: model, version: null };
  return {
    family: m[1],
    version: m[2].split(".").map(Number),
  };
}

function compareVersion(a: number[], b: number[]): number {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

export function filterActiveCohorts(cohorts: Cohort[]): Cohort[] {
  const nonEmpty = cohorts.filter((c) => c.total > 0 && !isDeprecatedFamily(c.model));
  const parsed: Parsed[] = nonEmpty.map((cohort) => {
    const { family, version } = parseModel(cohort.model);
    return { cohort, family, version };
  });

  const latestByFamily = new Map<string, number[]>();
  for (const { family, version } of parsed) {
    if (!version) continue;
    const cur = latestByFamily.get(family);
    if (!cur || compareVersion(version, cur) > 0) {
      latestByFamily.set(family, version);
    }
  }

  return parsed
    .filter(({ family, version }) => {
      const latest = latestByFamily.get(family);
      // No versioned cohorts in this family → keep everything in the family.
      if (!latest) return true;
      // Mixed-version family: drop unversioned + stale versions.
      return version != null && compareVersion(version, latest) === 0;
    })
    .map(({ cohort }) => cohort);
}
